import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRecorder } from '@/hooks/useRecorder';
import { useFeedStore } from '@/stores/feedStore';
import { useGroupStore } from '@/stores/groupStore';
import { useRateLimitStore } from '@/stores/rateLimitStore';
import * as blahblahsApi from '@/api/blahblahs';
import { RateLimitError } from '@/api/client';
import { cn, formatDuration } from '@/lib/utils';

const MAX_DURATION_MS = 60000; // 60 seconds

export function RecordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addBlahblah } = useFeedStore();
  const { postBlahblahToGroup } = useGroupStore();
  const { canUpload, getUploadCooldown, recordUpload } = useRateLimitStore();

  // Extract context from navigation state
  const locationState = location.state as {
    groupId?: string;
    groupName?: string;
    parentId?: string;
    parentBlahblah?: { id: string; user: { displayName: string } };
  } | null;
  const groupId = locationState?.groupId;
  const groupName = locationState?.groupName;
  const parentId = locationState?.parentId;
  const parentBlahblah = locationState?.parentBlahblah;
  const {
    isRecording,
    isPaused,
    duration,
    audioBlob,
    error,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
  } = useRecorder();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  // Update cooldown timer
  useEffect(() => {
    const updateCooldown = () => {
      setCooldown(getUploadCooldown());
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [getUploadCooldown]);

  // Auto-stop at max duration
  if (isRecording && !isPaused && duration >= MAX_DURATION_MS) {
    stopRecording();
  }

  const handleRecord = async () => {
    if (isRecording) {
      if (isPaused) {
        resumeRecording();
      } else {
        pauseRecording();
      }
    } else {
      await startRecording();
    }
  };

  const handleStop = async () => {
    await stopRecording();
  };

  const handleUpload = async () => {
    if (!audioBlob) return;

    // Check rate limit before attempting upload
    if (!canUpload()) {
      setUploadError(`Please wait ${cooldown} seconds before posting again`);
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const blahblah = await blahblahsApi.createBlahblah(audioBlob, duration, {
        parentId: parentId,
      });
      recordUpload(); // Record successful upload for rate limiting

      // Only add to feed if not a reply
      if (!parentId) {
        addBlahblah(blahblah);
      }

      // Auto-post to group if group context is present
      if (groupId) {
        try {
          await postBlahblahToGroup(groupId, blahblah.id);
        } catch {
          // Blahblah was created, group post failed silently
          console.warn('Failed to post to group, blahblah was still created');
        }
      }

      resetRecording();
      // Navigate back to appropriate page
      if (parentId) {
        navigate(`/blahblah/${parentId}`);
      } else if (groupId) {
        navigate(`/groups/${groupId}`);
      } else {
        navigate('/');
      }
    } catch (err) {
      if (err instanceof RateLimitError) {
        setUploadError(`Rate limited. Please wait ${err.retryAfter} seconds.`);
      } else {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setUploadError(message);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleDiscard = () => {
    resetRecording();
  };

  const progressPercent = (duration / MAX_DURATION_MS) * 100;

  return (
    <div className="min-h-[calc(100vh-7.5rem)] flex flex-col items-center justify-center px-4">
      {/* Reply banner */}
      {parentBlahblah && (
        <div className="mb-6 px-4 py-3 bg-primary/10 border border-primary/20 rounded-lg flex items-center gap-2">
          <ReplyIcon className="w-5 h-5 text-primary shrink-0" />
          <span className="text-sm text-muted-foreground">Replying to</span>
          <span className="text-sm font-semibold text-primary">{parentBlahblah.user.displayName}</span>
        </div>
      )}

      {/* Group banner */}
      {groupName && (
        <div className="mb-6 px-4 py-3 bg-primary/10 border border-primary/20 rounded-lg flex items-center gap-2">
          <GroupIcon className="w-5 h-5 text-primary shrink-0" />
          <span className="text-sm text-muted-foreground">Recording for</span>
          <span className="text-sm font-semibold text-primary">{groupName}</span>
        </div>
      )}

      {/* Error messages */}
      {(error || uploadError) && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error || uploadError}</p>
        </div>
      )}

      {/* Recording visualization */}
      <div className="relative mb-8">
        <div
          className={cn(
            'w-40 h-40 rounded-full flex items-center justify-center transition-all',
            isRecording && !isPaused
              ? 'bg-red-100 animate-pulse'
              : audioBlob
              ? 'bg-green-100'
              : 'bg-gray-100'
          )}
        >
          {isRecording && !isPaused ? (
            <div className="w-16 h-16 bg-red-500 rounded-full animate-pulse" />
          ) : audioBlob ? (
            <CheckIcon className="w-16 h-16 text-green-500" />
          ) : (
            <MicIcon className="w-16 h-16 text-gray-400" />
          )}
        </div>

        {/* Progress ring */}
        {(isRecording || duration > 0) && (
          <svg className="absolute inset-0 w-40 h-40 -rotate-90">
            <circle
              cx="80"
              cy="80"
              r="76"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              className="text-gray-200"
            />
            <circle
              cx="80"
              cy="80"
              r="76"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              strokeDasharray={`${progressPercent * 4.77} 477`}
              className={cn(
                duration > MAX_DURATION_MS * 0.9 ? 'text-red-500' : 'text-indigo-500'
              )}
            />
          </svg>
        )}
      </div>

      {/* Duration display */}
      <p className="text-3xl font-mono mb-8">
        {formatDuration(duration)} / {formatDuration(MAX_DURATION_MS)}
      </p>

      {/* Controls */}
      <div className="flex items-center gap-4">
        {!audioBlob ? (
          <>
            {isRecording && (
              <button
                onClick={handleStop}
                className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors"
              >
                <StopIcon className="w-6 h-6 text-gray-700" />
              </button>
            )}

            <button
              onClick={handleRecord}
              className={cn(
                'w-20 h-20 rounded-full flex items-center justify-center transition-all',
                isRecording
                  ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-indigo-500 hover:bg-indigo-600'
              )}
            >
              {isRecording ? (
                isPaused ? (
                  <PlayIcon className="w-8 h-8 text-white ml-1" />
                ) : (
                  <PauseIcon className="w-8 h-8 text-white" />
                )
              ) : (
                <MicIcon className="w-8 h-8 text-white" />
              )}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handleDiscard}
              disabled={isUploading}
              className="px-6 py-3 rounded-lg bg-gray-200 text-gray-700 font-medium hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              Discard
            </button>

            <button
              onClick={handleUpload}
              disabled={isUploading || cooldown > 0}
              className="px-6 py-3 rounded-lg bg-indigo-500 text-white font-medium hover:bg-indigo-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Uploading...
                </>
              ) : cooldown > 0 ? (
                `Wait ${cooldown}s`
              ) : (
                'Post'
              )}
            </button>
          </>
        )}
      </div>

      {/* Instructions */}
      <p className="mt-8 text-gray-500 text-sm text-center">
        {!isRecording && !audioBlob && 'Tap the mic to start recording'}
        {isRecording && !isPaused && 'Recording... Tap to pause'}
        {isRecording && isPaused && 'Paused. Tap to resume or stop'}
        {audioBlob && 'Ready to post your blahblah!'}
      </p>
    </div>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5.14v14l11-7-11-7z" />
    </svg>
  );
}

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
    </svg>
  );
}

function StopIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="6" width="12" height="12" rx="1" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

function GroupIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ReplyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
    </svg>
  );
}
